import { useEffect, useState } from 'react';
import axios from 'axios';
import { User, Mail, Phone, Calendar, CheckCircle, Clock, X, Copy, Check, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { WhatsAppInviteButton } from './WhatsAppInviteButton';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_LABELS = {
  raices: 'Raíces e infancia temprana (0-6)',
  infancia: 'Infancia (7-12)',
  adolescencia: 'Adolescencia (13-20)',
  juventud: 'Juventud (21-35)',
  madurez: 'Madurez (36-60)',
  actualidad: 'Actualidad',
};

/**
 * ContributorContactDialog
 *
 * Shows all the contact data the promoter has about a contributor so they can
 * reach out through other channels (email, phone from their own contribution,
 * etc.). Everything is read-only here — editing happens elsewhere.
 *
 * Props:
 *   - open, onOpenChange
 *   - contributorId
 *   - bookId  (needed to fetch the contribution form for phone/nickname)
 */
export default function ContributorContactDialog({ open, onOpenChange, contributorId, bookId, recipientName }) {
  const [contrib, setContrib] = useState(null);
  const [contribution, setContribution] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !contributorId || !bookId) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/books/${bookId}/contributors`, { withCredentials: true }),
      axios.get(`${API_URL}/api/books/${bookId}/contributions`, { withCredentials: true }),
    ]).then(([contribsRes, contribnsRes]) => {
      const found = (contribsRes.data || []).find(c => c.contributor_id === contributorId);
      setContrib(found || null);
      const matchedContribution = (contribnsRes.data || []).find(c => c.contributor_id === contributorId);
      setContribution(matchedContribution || null);
    }).catch(() => {
      setContrib(null);
      setContribution(null);
    }).finally(() => setLoading(false));
  }, [open, contributorId, bookId]);

  const copy = (val) => {
    if (!val) return;
    navigator.clipboard.writeText(val).then(() => toast.success('Copiado al portapapeles.'));
  };

  const who = contribution?.form_data?.who || {};
  const stages = contrib?.stages || [];
  const phone = who.contributor_phone || who.telefono || '';
  const nickname = who.contributor_nickname || '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="contributor-contact-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            {contrib ? `${contrib.name} ${contrib.last_name || ''}` : 'Colaborador'}
          </DialogTitle>
          <DialogDescription>
            Datos de contacto y estado de participación.
          </DialogDescription>
        </DialogHeader>

        {loading || !contrib ? (
          <div className="py-10 text-center text-muted-foreground text-sm">Cargando…</div>
        ) : (
          <div className="space-y-3 py-2">
            {/* Email */}
            <div className="flex items-center gap-3 p-3 rounded-md border bg-muted/30">
              <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="text-sm font-medium truncate" data-testid="contact-email">{contrib.email}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => copy(contrib.email)} className="h-7 w-7">
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>

            {/* Phone if provided */}
            {phone && (
              <div className="flex items-center gap-3 p-3 rounded-md border bg-muted/30">
                <Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Teléfono</p>
                  <p className="text-sm font-medium truncate">{phone}</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => copy(phone)} className="h-7 w-7">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}

            {/* Nickname */}
            {nickname && (
              <div className="flex items-center gap-3 p-3 rounded-md border bg-muted/30">
                <Star className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Apodo / Cómo le llaman</p>
                  <p className="text-sm font-medium">{nickname}</p>
                </div>
              </div>
            )}

            {/* Relationship */}
            <div className="flex items-center gap-3 p-3 rounded-md border bg-muted/30">
              <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground">Relación con el protagonista</p>
                <p className="text-sm font-medium capitalize">{contrib.relationship}</p>
              </div>
            </div>

            {/* Stages */}
            {stages.length > 0 && (
              <div className="p-3 rounded-md border bg-muted/30">
                <p className="text-xs text-muted-foreground mb-1.5">Etapas vitales que puede aportar</p>
                <div className="flex flex-wrap gap-1.5">
                  {stages.map(s => (
                    <span key={s} className="text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200">
                      {STAGE_LABELS[s]?.split(' (')[0] || s}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Status + timestamps */}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar className="w-3 h-3" />
                Invitado:{' '}
                <strong className="text-foreground">
                  {contrib.created_at ? new Date(contrib.created_at).toLocaleDateString('es-ES') : '—'}
                </strong>
              </div>
              <div className="flex items-center gap-2 text-xs">
                {contrib.has_contributed ? (
                  <><CheckCircle className="w-3 h-3 text-emerald-600" /> <span className="text-emerald-700">Ha aportado</span></>
                ) : (
                  <><Clock className="w-3 h-3 text-amber-600" /> <span className="text-amber-700">Pendiente</span></>
                )}
              </div>
            </div>

            {contrib.last_followup_at && (
              <div className="p-2 rounded-md bg-amber-50 border border-amber-200">
                <p className="text-[11px] text-amber-900">
                  Ya se le pidió más contenido el {new Date(contrib.last_followup_at).toLocaleDateString('es-ES')} — no se le volverá a reclamar desde la app.
                </p>
              </div>
            )}

            <div className="flex justify-between items-center pt-2 gap-2">
              <WhatsAppInviteButton
                name={contrib.name}
                inviteToken={contrib.invite_token}
                recipientName={recipientName || 'un ser querido'}
                phone={phone}
                testId="contact-whatsapp-btn"
              />
              <Button variant="outline" onClick={() => onOpenChange(false)} size="sm" className="gap-2">
                <X className="w-4 h-4" /> Cerrar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
